import type { TourRouteCoordinate } from '@/features/tour/tour-route-map-utils'
import type { TourValidationError } from '@/features/tour/tour-form-model/types'
import type { ITourTranslation, TourBasicInfoState } from '@/shared/api/types/tour-types'
import {
  MIN_TOUR_DURATION_MINUTES,
  MIN_TOUR_ROUTE_COORDINATES,
  TICKET_LINK_PATTERN,
} from '@/shared/constants/tour-form'
import { hasRequiredTranslations } from '@/shared/utils/language-utils'

/** An empty ticket link is fine (the field is optional); a filled one must be an http(s) URL. */
export function validateTicketLink(ticketLink: string): TourValidationError | null {
  const trimmed = ticketLink.trim()
  if (trimmed && !TICKET_LINK_PATTERN.test(trimmed)) {
    return {
      key: 'tour.ticketLinkInvalid',
      defaultValue: 'Link mua vé không hợp lệ. Phải bắt đầu bằng http:// hoặc https://',
    }
  }
  return null
}

/**
 * VI: Phạm vi tour phải có điểm đến của tenant; nếu chọn theo tỉnh thì còn phải có tỉnh.
 * EN: A tour must have a tenant destination; when scoped by province it also needs a province.
 */
export function validateTourScope(basicInfo: TourBasicInfoState): TourValidationError | null {
  if (!basicInfo.tenantDestinationId) {
    return {
      key: 'tour.tenantDestinationRequired',
      defaultValue: 'Vui lòng chọn điểm đến cho lộ trình.',
    }
  }
  if (basicInfo.scopeType === 'province' && !basicInfo.provinceId) {
    return {
      key: 'tour.provinceRequired',
      defaultValue: 'Vui lòng chọn tỉnh/thành phố cho lộ trình.',
    }
  }
  return null
}

/** Banner and thumbnail are both mandatory and must not be blank. */
export function validateTourImages(
  banner?: string,
  thumbnail?: string,
): TourValidationError | null {
  if (!banner?.trim() || !thumbnail?.trim()) {
    return { key: 'tour.imagesRequired' }
  }
  return null
}

export function validateTourTranslations(
  translations: ITourTranslation[],
): TourValidationError | null {
  return hasRequiredTranslations(translations, ['name', 'description'])
    ? null
    : { key: 'tour.allLanguagesTranslationRequired' }
}

export function validateRouteCoordinates(
  coordinates: TourRouteCoordinate[],
): TourValidationError | null {
  return coordinates.length < MIN_TOUR_ROUTE_COORDINATES
    ? { key: 'tour.coordinatesNeedTwoPoints' }
    : null
}

export interface TourSubmissionInput {
  /** Banner or thumbnail is still uploading. */
  isUploading: boolean
  /** Translations already trimmed by `normalizeTourTranslations`. */
  translations: ITourTranslation[]
  basicInfo: TourBasicInfoState
  banner?: string
  thumbnail?: string
  /** Final route coordinates (map edit, or inferred from the selected POIs). */
  coordinates: TourRouteCoordinate[]
}

/**
 * Validates the whole create form. Checks run in the order the user sees the sections, and the first
 * failure is returned so exactly one toast is shown.
 */
export function validateTourSubmission(input: TourSubmissionInput): TourValidationError | null {
  if (input.isUploading) {
    return { key: 'tour.waitMediaUpload' }
  }
  return (
    validateTourTranslations(input.translations) ??
    validateTourImages(input.banner, input.thumbnail) ??
    (input.basicInfo.duration < MIN_TOUR_DURATION_MINUTES ? { key: 'tour.durationMin30' } : null) ??
    validateTicketLink(input.basicInfo.ticketLink) ??
    validateTourScope(input.basicInfo) ??
    validateRouteCoordinates(input.coordinates)
  )
}
